import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";
import { Card, PageHeader } from "../ui";

const STORAGE_KEY = "sudler_contractor_session";

/**
 * Magic-link landing page. The contractor opens
 * /contractor/exchange?token=… from their invite email; we POST the
 * token to the exchange endpoint, stash the resulting scoped session
 * token in localStorage, and route them into the walkthrough portal.
 */
export function ContractorExchangePage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setError("No token in the link. Ask the unit owner to re-send the invite.");
      return;
    }
    void (async () => {
      try {
        const res = await fetch("/api/contractor/magic-link/exchange", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ token }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as {
            error?: string;
          };
          if (body.error === "expired" || body.error === "burned") {
            setError(
              "This invite link has expired or already been used. Ask the unit owner to issue a fresh invite.",
            );
          } else {
            setError(
              `We couldn't open this link (${body.error ?? `HTTP ${res.status}`}). Try again, or ask the unit owner to re-send the invite.`,
            );
          }
          return;
        }
        const body = (await res.json()) as {
          sessionToken: string;
          projectId: string;
          contractorCompanyId: string;
          expiresAt: string;
        };
        try {
          localStorage.setItem(STORAGE_KEY, body.sessionToken);
        } catch {
          // private mode / disabled storage — keep going; the portal
          // page surfaces the same error.
        }
        nav("/contractor/portal", { replace: true });
      } catch (err) {
        setError(String(err));
      }
    })();
  }, [params, nav]);

  return (
    <div className="max-w-lg mx-auto pt-12 px-4">
      <PageHeader title="Contractor sign-in" />
      <Card>
        {error ? (
          <div className="text-center py-6">
            <AlertTriangle
              size={28}
              className="text-status-rejected-fg mx-auto mb-3"
            />
            <p className="text-[14px] text-ink-700 leading-relaxed">{error}</p>
          </div>
        ) : (
          <div className="text-center py-6">
            <Loader2
              size={28}
              className="text-brand-700 mx-auto mb-3 animate-spin"
            />
            <p className="text-[14px] text-ink-700">Opening your invite…</p>
          </div>
        )}
      </Card>
    </div>
  );
}

void CheckCircle2;
